import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useDeliveryPreferencesStore } from "../deliveryPreferencesStore";
import { useOrderLinkFlowOrder } from "~/hooks/api/v2/orderLinkFlow/useOrderLinkFlowOrder";
import { ORDER_DETAILS } from "~/constants/routes";
import { DistributionSupplierBrowseList } from "../../components/DistributionSupplierBrowseList";

const DELIVERY_TIME_LABELS: Record<string, string> = {
  AM: "AM delivery slot",
  EARLY_PM: "Early PM delivery slot",
  FLEXIBLE: "Flexible delivery slot",
};

export default function CompletionStep() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const store = useDeliveryPreferencesStore();
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const { data: order } = useOrderLinkFlowOrder(token);

  useEffect(() => {
    if (redirectUrl) {
      window.location.replace(redirectUrl);
    }
  }, [redirectUrl]);

  const onClickViewOrder = (orderId: number) => {
    setRedirectUrl(`${ORDER_DETAILS.href({ orderId })}?tab=All+orders`);
  };

  const contact = store.deliveryContact;
  const deliveryTimeLabel = store.deliveryTime
    ? DELIVERY_TIME_LABELS[store.deliveryTime]
    : null;

  return (
    <div className="min-h-screen bg-neutral-100 px-5 py-10">
      <div className="mx-auto w-full max-w-md">
        {/* Success header */}
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          {/* Check icon svg */}
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-800">
              <svg
                className="h-5 w-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-zinc-800">
              Your delivery preferences are saved
            </h2>
            <p className="mt-1 text-sm text-zinc-500">
              You can change your settings at any time
            </p>
          </div>
        </div>

        {/* Delivery preferences summary card */}
        <div className="mb-6 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-zinc-200">
          <p className="mb-3 text-sm font-semibold text-zinc-800">
            Delivery preferences
          </p>
          {deliveryTimeLabel && (
            <div className="mb-2 flex items-center gap-2 text-sm text-zinc-700">
              <svg
                className="h-4 w-4 shrink-0 text-zinc-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z"
                />
              </svg>
              {deliveryTimeLabel}
            </div>
          )}
          {contact && (
            <div>
              <p className="text-sm font-semibold text-zinc-800">
                {contact.name}
              </p>
              <p className="text-sm text-zinc-500">
                {contact.email}
                {contact.phone ? ` • ${contact.phone}` : ""}
              </p>
            </div>
          )}

          {!!order && (
            <div className="mt-4 text-center">
              <p className="text-sm text-zinc-600">
                <button
                  type="button"
                  onClick={() => onClickViewOrder(order.orderId)}
                  className="cursor-pointer text-blue-600 hover:underline"
                >
                  View order
                </button>
              </p>
            </div>
          )}
        </div>

        {!!order && (
          <div className="mt-6">
            <div className="mb-5">
              <h2 className="text-xl font-semibold text-zinc-800">
                Add more to your delivery
              </h2>
              <p className="mt-1 text-sm text-zinc-500">
                Browse suppliers delivered by Sellar.
              </p>
            </div>
            <DistributionSupplierBrowseList
              buyerPostcode={order.retailerPostalCode}
              excludeSupplierId={order.supplierId}
            />
          </div>
        )}
      </div>
    </div>
  );
}
